/**
 * Telegram-style "select messages" mode.
 *
 * State lives on `chatbox.ui` (a plain, non-persisted skeletor Model already
 * used for transient view state), so every component that already reacts to
 * `chatbox.ui` changes (e.g. converse-chat-content) picks this up for free.
 */

/**
 * @param {import('@converse/headless').ChatBox|import('@converse/headless').ChatRoom} chatbox
 * @returns {boolean}
 */
export function isSelectingMessages(chatbox) {
    return !!chatbox?.ui?.get('selecting_messages');
}

/**
 * @param {import('@converse/headless').ChatBox|import('@converse/headless').ChatRoom} chatbox
 * @returns {string[]}
 */
export function getSelectedMsgIds(chatbox) {
    return chatbox?.ui?.get('selected_msgids') || [];
}

/**
 * @param {import('@converse/headless').ChatBox|import('@converse/headless').ChatRoom} chatbox
 * @param {string} msgid
 * @returns {boolean}
 */
export function isMessageSelected(chatbox, msgid) {
    return getSelectedMsgIds(chatbox).includes(msgid);
}

/**
 * Enters selection mode with a single message pre-selected.
 * @param {import('@converse/headless').ChatBox|import('@converse/headless').ChatRoom} chatbox
 * @param {string} msgid
 */
export function startSelecting(chatbox, msgid) {
    chatbox.ui.set({
        'selecting_messages': true,
        'selected_msgids': [msgid],
    });
}

/**
 * @param {import('@converse/headless').ChatBox|import('@converse/headless').ChatRoom} chatbox
 * @param {string} msgid
 */
export function toggleMessageSelection(chatbox, msgid) {
    const current = getSelectedMsgIds(chatbox);
    const next = current.includes(msgid) ? current.filter((id) => id !== msgid) : [...current, msgid];

    if (next.length === 0) {
        stopSelecting(chatbox);
    } else {
        chatbox.ui.set({ 'selecting_messages': true, 'selected_msgids': next });
    }
}

/**
 * @param {import('@converse/headless').ChatBox|import('@converse/headless').ChatRoom} chatbox
 */
export function stopSelecting(chatbox) {
    chatbox.ui.set({ 'selecting_messages': false, 'selected_msgids': [] });
}

/**
 * Returns the selected messages, in chronological order.
 * @param {import('@converse/headless').ChatBox|import('@converse/headless').ChatRoom} chatbox
 * @returns {Array<import('@converse/headless').Message>}
 */
export function getSelectedMessages(chatbox) {
    const ids = new Set(getSelectedMsgIds(chatbox));
    if (!ids.size) return [];
    return chatbox.messages.models.filter((m) => ids.has(m.get('msgid')));
}
